import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lock,
  Sparkles,
  CheckCircle,
  XCircle,
  LayoutGrid,
  Clock,
  Loader2,
  Save,
  Send,
  AlertTriangle,
  LogOut,
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useContentStore } from '../store/contentStore';
import AuthPage from './AuthPage';
import CategoriesPanel from './admin/CategoriesPanel';
import ScenariosPanel from './admin/ScenariosPanel';
import { BUNDLES } from '../data/bundles';
import type { AgeGroup, CategoryId, CommunitySubmission } from '../types';

// ── Mock pending submissions for moderation queue ─────────────────
// TODO: Replace with real API: GET /api/admin/submissions?status=pending
const MOCK_PENDING: CommunitySubmission[] = [
  {
    id: 'pend-001',
    submittedBy: 'user-aaa',
    submitterName: 'Laura K.',
    submittedAt: '2024-03-18',
    title: 'Finding your way if your phone dies',
    ageGroup: '11-13',
    category: 'travel-public',
    scenarioText:
      "You're out on your own and your phone battery dies. You don't know exactly where you are. What do you do?",
    discussionPrompts: [
      "Who could you ask for help if your phone died?",
      "What information do you need to know before going out alone?",
    ],
    safetyTip: "Always tell a trusted adult where you're going and when you expect to be back.",
    tags: ['navigation', 'phone', 'being alone'],
    upvotes: 5,
    status: 'pending',
  },
  {
    id: 'pend-002',
    submittedBy: 'user-bbb',
    submitterName: 'Mike R.',
    submittedAt: '2024-03-19',
    title: 'Someone pushes you in the playground',
    ageGroup: '5-7',
    category: 'dealing-with-conflict',
    scenarioText:
      'Another child keeps pushing you in the playground at lunch. What should you do?',
    discussionPrompts: [
      "What is the difference between an accident and bullying?",
      "Who at school is safe to tell about this?",
    ],
    tags: ['playground', 'bullying', 'physical'],
    upvotes: 12,
    status: 'pending',
  },
  {
    id: 'pend-003',
    submittedBy: 'user-ccc',
    submitterName: 'Priya S.',
    submittedAt: '2024-03-20',
    title: 'Being pressured to share your location',
    ageGroup: '8-10',
    category: 'online-safety-digital',
    scenarioText:
      'A friend you met online asks you to share your location so they can "come visit you". What do you do?',
    discussionPrompts: [
      "Why might sharing your location be dangerous?",
      "What would you say to this person?",
      "Who should you tell about this conversation?",
    ],
    safetyTip: "Never share your home address or live location with anyone you have only met online.",
    tags: ['location sharing', 'online friends', 'stranger danger'],
    submitterNote: "This situation is increasingly common with gaming apps — children need practical language to refuse.",
    upvotes: 19,
    status: 'pending',
  },
];

interface GeneratedScenario {
  title: string;
  scenarioDescription: string;
  revealAnswer: string;
  memoryPhrase: string;
  actionSteps: string[];
  whatChildCanSay: string;
  parentCoachingTip: string;
}

export default function Admin() {
  const navigate = useNavigate();
  const { accounts, currentAccountId, logout, syncAdminRoles } = useAuthStore();
  const currentAccount = accounts.find((a) => a.id === currentAccountId) ?? null;
  const { categories, scenarios } = useContentStore();

  useEffect(() => { syncAdminRoles(); }, [syncAdminRoles]);

  const [activeTab, setActiveTab] = useState<'overview' | 'categories' | 'scenarios' | 'moderation'>('overview');

  // AI generator state
  const [prompt, setPrompt] = useState('');
  const [genCategory, setGenCategory] = useState<CategoryId | ''>('');
  const [genAge, setGenAge] = useState<AgeGroup>('7-9');
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState<GeneratedScenario | null>(null);
  const [genError, setGenError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<'draft' | 'published' | null>(
    null
  );

  // Moderation queue state
  const [pending, setPending] = useState<CommunitySubmission[]>(MOCK_PENDING);
  const [moderatingId, setModeratingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Not logged in — show auth form
  if (!currentAccount) {
    return <AuthPage onSuccess={() => {}} />;
  }

  // Logged in but not yet admin
  if (currentAccount.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-6 text-center bg-cream">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-3xl p-8 shadow-card max-w-xs w-full"
        >
          <Lock size={48} className="text-gray-300 mx-auto mb-4" />
          <h2 className="font-display text-2xl font-extrabold text-gray-700 mb-2">
            Pending Admin Access
          </h2>
          <p className="text-gray-500 text-sm mb-2">
            Your account is registered but doesn't have admin privileges yet.
          </p>
          <p className="text-xs text-gray-400 bg-gray-50 rounded-xl px-4 py-3 mb-6 font-mono break-all">
            {currentAccount.email}
          </p>
          <p className="text-gray-400 text-xs mb-6">
            Share the email above to have admin access granted, then log back in.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => { logout(); }}
              className="flex items-center justify-center gap-2 border-2 border-gray-200 text-gray-600 px-6 py-3 rounded-2xl font-bold text-sm hover:bg-gray-50 transition-colors"
            >
              <LogOut size={15} />
              Log Out
            </button>
            <button
              onClick={() => navigate('/')}
              className="bg-primary-600 text-white px-6 py-3 rounded-2xl font-bold text-sm hover:bg-primary-700 transition-colors"
            >
              Go Home
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  const pendingCount = pending.length;
  const totalScenarios = scenarios.length;
  const totalBundles = BUNDLES.length;
  const totalCategories = categories.length;

  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setGenError('');
    setGenerated(null);
    setSaveSuccess(null);

    try {
      const res = await fetch('/api/ai-generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, categoryId: genCategory || 'general', ageGroup: genAge }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setGenerated({
        title: data.title,
        scenarioDescription: data.scenarioDescription,
        revealAnswer: data.revealAnswer,
        memoryPhrase: data.memoryPhrase,
        actionSteps: data.actionSteps?.map((s: { text: string }) => s.text) ?? [],
        whatChildCanSay: data.whatChildCanSay,
        parentCoachingTip: data.parentCoachingTip,
      });
    } catch (err) {
      setGenError(err instanceof Error ? err.message : 'Failed to generate scenario. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  // TODO: Wire to real API: POST /api/admin/scenarios (draft or publish)
  const handleSave = async (mode: 'draft' | 'published') => {
    setSaving(true);
    await new Promise((r) => setTimeout(r, 700));
    setSaving(false);
    setSaveSuccess(mode);
  };

  // TODO: Wire to real API: PATCH /api/admin/submissions/:id
  const handleModerate = async (
    submissionId: string,
    _action: 'approved' | 'rejected'
  ) => {
    setModeratingId(submissionId);
    await new Promise((r) => setTimeout(r, 500));
    setPending((prev) => prev.filter((s) => s.id !== submissionId));
    setModeratingId(null);
  };

  return (
    <div className="max-w-lg mx-auto pb-12">
      {/* Header */}
      <div className="px-4 pt-8 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-extrabold text-gray-800 flex items-center gap-2">
              <LayoutGrid size={22} className="text-primary-600" />
              Admin Panel
            </h1>
            <p className="text-gray-400 text-sm mt-0.5">
              {currentAccount?.name} · {currentAccount?.email}
            </p>
          </div>
          <button
            onClick={() => { logout(); navigate('/'); }}
            className="flex items-center gap-1.5 text-gray-400 hover:text-gray-600 text-xs font-semibold"
          >
            <LogOut size={14} /> Log out
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-4 gap-2 px-4 mb-4">
        {[
          { label: 'Categories', value: totalCategories, color: 'text-indigo-600' },
          { label: 'Scenarios', value: totalScenarios, color: 'text-primary-700' },
          { label: 'Bundles', value: totalBundles, color: 'text-violet-700' },
          { label: 'Pending', value: pendingCount, color: 'text-amber-600' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-2.5 shadow-card text-center">
            <p className={`font-display font-extrabold text-xl ${s.color}`}>{s.value}</p>
            <p className="text-gray-500 text-[10px] font-semibold mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-gray-100 mb-5 px-4 gap-1 overflow-x-auto scrollbar-none">
        {([
          { id: 'overview',    label: 'Overview',    icon: '🏠' },
          { id: 'categories',  label: 'Categories',  icon: '🗂️' },
          { id: 'scenarios',   label: 'Scenarios',   icon: '📖' },
          { id: 'moderation',  label: 'Moderation',  icon: '🔍' },
        ] as const).map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-bold whitespace-nowrap border-b-2 transition-all ${
              activeTab === t.id
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div className="px-4 space-y-7">

      {/* ── CATEGORIES TAB ─────────────────────────────────── */}
      {activeTab === 'categories' && <CategoriesPanel />}

      {/* ── SCENARIOS TAB ──────────────────────────────────── */}
      {activeTab === 'scenarios' && <ScenariosPanel />}

      {/* ── OVERVIEW TAB ───────────────────────────────────── */}
      {activeTab === 'overview' && <>

      {/* AI Scenario Generator */}
      <section className="bg-white rounded-3xl shadow-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-xl bg-primary-100 flex items-center justify-center">
            <Sparkles size={16} className="text-primary-600" />
          </div>
          <h2 className="font-display font-bold text-gray-800">
            AI Scenario Generator
          </h2>
        </div>

        <div className="space-y-4">
          {/* Prompt */}
          <div>
            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
              Prompt *
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe the scenario to generate, e.g. 'A child is approached by a stranger in a car park...'"
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 placeholder-gray-300 outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 resize-none"
            />
          </div>

          {/* Category + Age row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                Category
              </label>
              <select
                value={genCategory}
                onChange={(e) =>
                  setGenCategory(e.target.value as CategoryId | '')
                }
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-primary-200 bg-white"
              >
                <option value="">Any</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.icon} {cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                Age Range
              </label>
              <select
                value={genAge}
                onChange={(e) => setGenAge(e.target.value as AgeGroup)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-primary-200 bg-white"
              >
                <option value="4-6">4–6 years</option>
                <option value="7-9">7–9 years</option>
                <option value="10-12">10–12 years</option>
                <option value="all">All ages</option>
              </select>
            </div>
          </div>

          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={handleGenerate}
            disabled={generating || !prompt.trim()}
            className="w-full bg-primary-600 text-white py-3.5 rounded-2xl font-bold text-sm shadow-md hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {generating ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Generating…
              </>
            ) : (
              <>
                <Sparkles size={16} />
                Generate Scenario
              </>
            )}
          </motion.button>

          {genError && (
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 rounded-xl p-3">
              <AlertTriangle size={15} />
              {genError}
            </div>
          )}
        </div>

        {/* Generated preview */}
        <AnimatePresence>
          {generated && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              className="mt-5 space-y-3 border-t border-gray-100 pt-5"
            >
              <h3 className="font-display font-bold text-gray-700 text-sm">
                Generated Preview
              </h3>

              {/* Editable fields */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={generated.title}
                    onChange={(e) =>
                      setGenerated((g) =>
                        g ? { ...g, title: e.target.value } : g
                      )
                    }
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-1">
                    Scenario Description
                  </label>
                  <textarea
                    value={generated.scenarioDescription}
                    onChange={(e) =>
                      setGenerated((g) =>
                        g
                          ? { ...g, scenarioDescription: e.target.value }
                          : g
                      )
                    }
                    rows={3}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-primary-200 resize-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-1">
                    Reveal Answer
                  </label>
                  <textarea
                    value={generated.revealAnswer}
                    onChange={(e) =>
                      setGenerated((g) =>
                        g ? { ...g, revealAnswer: e.target.value } : g
                      )
                    }
                    rows={2}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-primary-200 resize-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-1">
                    Memory Phrase
                  </label>
                  <input
                    type="text"
                    value={generated.memoryPhrase}
                    onChange={(e) =>
                      setGenerated((g) =>
                        g ? { ...g, memoryPhrase: e.target.value } : g
                      )
                    }
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-1">
                    What Child Can Say
                  </label>
                  <input
                    type="text"
                    value={generated.whatChildCanSay}
                    onChange={(e) =>
                      setGenerated((g) =>
                        g ? { ...g, whatChildCanSay: e.target.value } : g
                      )
                    }
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-400 mb-1">
                    Parent Coaching Tip
                  </label>
                  <textarea
                    value={generated.parentCoachingTip}
                    onChange={(e) =>
                      setGenerated((g) =>
                        g ? { ...g, parentCoachingTip: e.target.value } : g
                      )
                    }
                    rows={2}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-primary-200 resize-none"
                  />
                </div>
              </div>

              {saveSuccess && (
                <div className="flex items-center gap-2 bg-green-50 text-green-700 text-sm font-semibold rounded-xl p-3">
                  <CheckCircle size={15} />
                  {saveSuccess === 'draft'
                    ? 'Saved as draft!'
                    : 'Published successfully!'}
                </div>
              )}

              <div className="flex gap-3">
                <motion.button
                  whileTap={{ scale: 0.96 }}
                  onClick={() => handleSave('draft')}
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-1.5 border-2 border-gray-200 text-gray-600 py-3 rounded-2xl font-bold text-sm hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <Save size={14} />
                  Save as Draft
                </motion.button>
                <motion.button
                  whileTap={{ scale: 0.96 }}
                  onClick={() => handleSave('published')}
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-green-500 text-white py-3 rounded-2xl font-bold text-sm hover:bg-green-600 transition-colors disabled:opacity-50 shadow-md"
                >
                  {saving ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Send size={14} />
                  )}
                  Publish
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      </> /* end overview tab */}

      {/* ── MODERATION TAB ─────────────────────────────────── */}
      {activeTab === 'moderation' && <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-gray-800 flex items-center gap-2">
            <Clock size={16} className="text-amber-500" />
            Scenario Submissions
          </h2>
          <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2.5 py-1 rounded-full">
            {pending.length} pending
          </span>
        </div>
        <p className="text-gray-400 text-xs mb-4">
          Sorted oldest first. Click a card to see all fields before moderating.
        </p>

        {pending.length === 0 ? (
          <div className="bg-white rounded-2xl p-6 shadow-card text-center text-gray-400">
            <CheckCircle size={32} className="mx-auto mb-2 text-green-400" />
            <p className="font-semibold text-gray-500 text-sm">
              All caught up! No pending submissions.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {[...pending]
              .sort((a, b) =>
                (a.submittedAt ?? a.createdAt ?? '').localeCompare(b.submittedAt ?? b.createdAt ?? '')
              )
              .map((sub) => {
              const cat = categories.find((c) => c.id === (sub.category ?? sub.categoryId));
              const isModerating = moderatingId === sub.id;
              const isExpanded = expandedId === sub.id;

              return (
                <motion.div
                  key={sub.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="bg-white rounded-2xl shadow-card overflow-hidden"
                >
                  {/* Summary row */}
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : sub.id)}
                    className="w-full text-left p-4"
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      {cat && <span className="text-base">{cat.icon}</span>}
                      <span className="text-xs font-semibold text-gray-400 flex-1">
                        {cat?.name ?? sub.category}
                      </span>
                      <span className="bg-gray-100 text-gray-500 text-xs font-semibold px-2 py-0.5 rounded-full">
                        {sub.ageGroup}
                      </span>
                    </div>
                    <h3 className="font-display font-bold text-gray-800 text-sm leading-snug">
                      {sub.title}
                    </h3>
                    <p className="text-gray-400 text-xs mt-1">
                      by {sub.submitterName} · {sub.upvotes ?? sub.votes ?? 0} upvotes · {(sub.submittedAt ?? sub.createdAt ?? '').slice(0, 10)}
                    </p>
                  </button>

                  {/* Expanded detail view */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-gray-100 px-4 pb-4 pt-3 space-y-3">
                          <div>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">Scenario</p>
                            <p className="text-gray-700 text-sm leading-relaxed">
                              {sub.scenarioText ?? sub.scenarioDescription}
                            </p>
                          </div>

                          {sub.discussionPrompts && sub.discussionPrompts.length > 0 && (
                            <div>
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">Discussion Prompts</p>
                              <ol className="list-decimal list-inside space-y-0.5">
                                {sub.discussionPrompts.map((p, i) => (
                                  <li key={i} className="text-gray-700 text-xs">{p}</li>
                                ))}
                              </ol>
                            </div>
                          )}

                          {sub.safetyTip && (
                            <div>
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">Safety Tip</p>
                              <p className="text-gray-700 text-xs">{sub.safetyTip}</p>
                            </div>
                          )}

                          {sub.tags && sub.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {sub.tags.map((tag) => (
                                <span key={tag} className="bg-gray-100 text-gray-500 text-[10px] font-semibold px-2 py-0.5 rounded-full">{tag}</span>
                              ))}
                            </div>
                          )}

                          {sub.submitterNote && (
                            <div>
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">Why valuable (submitter)</p>
                              <p className="text-gray-600 text-xs italic">"{sub.submitterNote}"</p>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Action buttons */}
                  <div className="flex gap-2 px-4 pb-4">
                    <button
                      onClick={() => handleModerate(sub.id, 'rejected')}
                      disabled={isModerating}
                      className="flex-1 flex items-center justify-center gap-1.5 border border-red-200 text-red-600 py-2.5 rounded-xl font-semibold text-xs hover:bg-red-50 transition-colors disabled:opacity-50"
                    >
                      {isModerating ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : (
                        <XCircle size={13} />
                      )}
                      Reject
                    </button>
                    <button
                      onClick={() => handleModerate(sub.id, 'approved')}
                      disabled={isModerating}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-green-500 text-white py-2.5 rounded-xl font-semibold text-xs hover:bg-green-600 transition-colors disabled:opacity-50 shadow-sm"
                    >
                      {isModerating ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : (
                        <CheckCircle size={13} />
                      )}
                      Approve → Library
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </section>} {/* end moderation tab */}

      </div>
    </div>
  );
}
